import { useEffect, useRef, useState } from 'react'
import { useMutation } from 'convex/react'
import { Link } from 'react-router-dom'
import { Copy, Loader2 } from 'lucide-react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

type PriceListOverview = {
  organizationServiceCount: number
  clinics: {
    companyId: Id<'companies'>
    nome: string
    serviceCount: number
    personalServiceCount: number
  }[]
}

type OrganizationPriceListAdoptionProps = {
  organizationId: Id<'organizations'>
  overview: PriceListOverview | undefined
  sharingEnabled: boolean
  disabled: boolean
  onBusyChange: (busy: boolean) => void
}

export function OrganizationPriceListAdoption({
  organizationId,
  overview,
  sharingEnabled,
  disabled,
  onBusyChange,
}: OrganizationPriceListAdoptionProps) {
  const adoptClinicPriceList = useMutation(
    api.organizations.adoptClinicPriceList
  )
  const [selectedClinicId, setSelectedClinicId] =
    useState<Id<'companies'> | null>(null)
  const [adopting, setAdopting] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')
  const confirmationRef = useRef<HTMLDivElement>(null)
  const statusRef = useRef<HTMLParagraphElement>(null)
  const sourceButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const selectedClinic = overview?.clinics.find(
    (clinic) => clinic.companyId === selectedClinicId
  )
  const busy = disabled || adopting

  useEffect(() => {
    if (selectedClinicId) confirmationRef.current?.focus()
  }, [selectedClinicId])

  useEffect(() => {
    if (status) statusRef.current?.focus()
  }, [status])

  const cancel = () => {
    if (busy) return
    const sourceId = selectedClinicId
    setSelectedClinicId(null)
    setError('')
    if (sourceId) sourceButtonRefs.current[sourceId]?.focus()
  }

  const adopt = async () => {
    if (!selectedClinic || selectedClinic.serviceCount === 0 || busy) return
    setAdopting(true)
    onBusyChange(true)
    setError('')
    setStatus('')
    try {
      const result = await adoptClinicPriceList({
        organizationId,
        companyId: selectedClinic.companyId,
      })
      const count = result.adoptedServiceCount
      setStatus(
        `Listino della società aggiornato con ${count} ${count === 1 ? 'prestazione' : 'prestazioni'} della clinica “${selectedClinic.nome}”. ` +
          (result.replacedServiceCount > 0
            ? result.replacedServiceCount === 1
              ? 'La prestazione del precedente listino societario è stata disattivata. '
              : `Le ${result.replacedServiceCount} prestazioni del precedente listino societario sono state disattivate. `
            : '') +
          (sharingEnabled
            ? 'La condivisione resta attiva: le nuove prescrizioni delle cliniche useranno questo listino.'
            : 'La condivisione resta disattivata. Puoi attivarla e salvare la società quando il listino è pronto.')
      )
      setSelectedClinicId(null)
    } catch (caught) {
      const rawMessage = caught instanceof Error ? caught.message : ''
      const knownMessage = [
        'La clinica deve appartenere alla società selezionata',
        'La clinica non ha voci attive nel proprio listino; i listini personali sono esclusi',
        "L'adozione supporta al massimo 500 voci per listino",
      ].find((message) => rawMessage.includes(message))
      setError(
        knownMessage ??
          (rawMessage && rawMessage.length < 300
            ? rawMessage
            : 'Non è stato possibile usare il listino della clinica. Verifica che contenga prestazioni attive e riprova.')
      )
    } finally {
      setAdopting(false)
      onBusyChange(false)
    }
  }

  return (
    <section
      aria-labelledby="organization-source-lists-title"
      className="space-y-4 border-t border-gray-200 pt-5"
    >
      <div>
        <h3
          id="organization-source-lists-title"
          className="text-lg font-semibold text-gray-950"
        >
          Usa il listino di una clinica
        </h3>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
          Scegli una clinica come base per il listino della società. Verranno
          copiate tutte le sue prestazioni attive, esclusi i listini personali
          dei medici. I listini delle altre cliniche non vengono aggiunti.
        </p>
      </div>

      {overview === undefined ? (
        <p
          role="status"
          className="flex items-center gap-2 py-4 text-sm text-gray-600"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Caricamento dei listini delle cliniche…
        </p>
      ) : overview.clinics.length === 0 ? (
        <p className="py-3 text-sm leading-6 text-gray-600">
          Collega prima una clinica alla società per usarne il listino. Puoi
          anche aggiungere direttamente prestazioni al listino della società.
        </p>
      ) : (
        <Card className="overflow-hidden shadow-none">
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">
              Prestazioni attive nei listini delle cliniche collegate
            </caption>
            <thead className="hidden bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-600 lg:table-header-group">
              <tr>
                <th scope="col" className="px-4 py-3">
                  Clinica
                </th>
                <th scope="col" className="px-4 py-3">
                  Prestazioni da copiare
                </th>
                <th scope="col" className="px-4 py-3">
                  Personali escluse
                </th>
                <th scope="col" className="px-4 py-3 text-right">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="block divide-y divide-gray-200 lg:table-row-group">
              {overview.clinics.map((clinic) => (
                <tr
                  key={clinic.companyId}
                  className="block bg-white lg:table-row"
                >
                  <th
                    scope="row"
                    className="block px-4 pb-2 pt-4 text-left font-normal lg:table-cell lg:py-4"
                  >
                    <span className="break-words text-sm font-semibold text-gray-950">
                      {clinic.nome}
                    </span>
                  </th>
                  <td className="block px-4 py-1 text-sm text-gray-700 lg:table-cell lg:py-4">
                    <span className="mr-2 lg:sr-only">
                      Prestazioni da copiare:
                    </span>
                    <span className="font-medium tabular-nums">
                      {clinic.serviceCount}
                    </span>
                  </td>
                  <td className="block px-4 py-1 text-sm text-gray-700 lg:table-cell lg:py-4">
                    <span className="mr-2 lg:sr-only">Personali escluse:</span>
                    <span className="tabular-nums">
                      {clinic.personalServiceCount}
                    </span>
                  </td>
                  <td className="block px-4 pb-4 pt-3 lg:table-cell lg:py-4">
                    <div className="flex flex-col gap-2 lg:items-end">
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="min-h-10"
                      >
                        <Link
                          to={`/listino?companyId=${clinic.companyId}`}
                          aria-label={`Apri il listino della clinica ${clinic.nome}`}
                        >
                          Apri listino clinica
                        </Link>
                      </Button>
                      <Button
                        ref={(node) => {
                          sourceButtonRefs.current[clinic.companyId] = node
                        }}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="min-h-10 whitespace-normal text-left"
                        disabled={busy || clinic.serviceCount === 0}
                        aria-expanded={selectedClinicId === clinic.companyId}
                        aria-controls="organization-adoption-confirmation"
                        aria-label={`Usa il listino di ${clinic.nome} come listino della società`}
                        onClick={() => {
                          setSelectedClinicId(clinic.companyId)
                          setError('')
                          setStatus('')
                        }}
                      >
                        <Copy
                          className="mr-2 h-4 w-4 shrink-0"
                          aria-hidden="true"
                        />
                        Usa come listino della società
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {selectedClinic && (
        <div
          id="organization-adoption-confirmation"
          ref={confirmationRef}
          tabIndex={-1}
          role="group"
          aria-labelledby="organization-adoption-title"
          className="space-y-3 rounded-lg border border-gray-300 bg-gray-50 p-5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <h4
            id="organization-adoption-title"
            className="text-base font-semibold text-gray-950"
          >
            Usare il listino di “{selectedClinic.nome}” per la società?
          </h4>
          <ul className="max-w-2xl list-disc space-y-2 pl-5 text-sm leading-6 text-gray-700">
            <li>
              {selectedClinic.serviceCount === 1
                ? 'Verrà copiata la prestazione attiva della clinica.'
                : `Verranno copiate ${selectedClinic.serviceCount} prestazioni attive della clinica.`}{' '}
              Le successive modifiche al listino della clinica non aggiornano
              questa copia.
            </li>
            <li>
              {overview && overview.organizationServiceCount > 0
                ? `Il listino attuale della società (${overview.organizationServiceCount} ${overview.organizationServiceCount === 1 ? 'prestazione attiva' : 'prestazioni attive'}) sarà sostituito dalla copia, disattivato e conservato per lo storico.`
                : 'La copia diventerà il listino della società.'}
            </li>
            <li>
              I listini personali dei medici sono esclusi. I listini originali
              delle cliniche e dei medici restano salvati.
            </li>
            <li>
              {sharingEnabled
                ? 'La condivisione è attiva: il nuovo listino si applicherà subito a tutte le nuove prescrizioni delle cliniche collegate. Le prescrizioni già create non cambiano.'
                : 'La condivisione resterà disattivata. Le cliniche continueranno a usare i propri listini finché non la attivi e salvi la società.'}
            </li>
          </ul>
          {error && (
            <p role="alert" className="text-sm text-red-800">
              {error}
            </p>
          )}
          <div className="flex flex-col-reverse gap-3 pt-1 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={cancel}
            >
              Annulla
            </Button>
            <Button
              type="button"
              disabled={busy || selectedClinic.serviceCount === 0}
              onClick={() => void adopt()}
            >
              {adopting && (
                <Loader2
                  className="mr-2 h-4 w-4 animate-spin"
                  aria-hidden="true"
                />
              )}
              {adopting
                ? 'Preparazione del listino…'
                : 'Conferma e usa questo listino'}
            </Button>
          </div>
        </div>
      )}
      {status && (
        <p
          ref={statusRef}
          tabIndex={-1}
          role="status"
          className="rounded-md py-1 text-sm leading-6 text-green-800 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          {status}
        </p>
      )}
    </section>
  )
}
